import { ROLE_RANK, type Capability } from "../stores/siteStore";

/**
 * What a surface requires before a user may see it (ADR 120/175, generalised by
 * ADR 274).
 *
 * This started life as the nav's gate and is now the shared one: a nav item and
 * a home widget are the same question — *may this user, on this site, in this
 * system's current state, see this thing?* — and answering it twice is how the
 * two drift apart. A widget catalogue whose gate is a second implementation is
 * a tenancy bug waiting for its first divergence.
 */
export interface Gate {
  requiresCapability?: Capability;
  /**
   * Visible if the active site has ANY of these capabilities — e.g. /live serves both
   * an RTLS floor and an inventory warehouse. Checked after the global-admin bypass,
   * like requiresCapability (which is the single-capability form).
   */
  requiresAnyCapability?: Capability[];
  minRole?: "viewer" | "supervisor" | "site-admin" | "global-admin";
  /**
   * Hide until the system is provisioned enough for the thing to do anything
   * (ADR 175/178). "tenant" = needs ≥1 tenant the user can act on; "site" = needs
   * ≥1 site in scope. Checked BEFORE the global-admin bypass — system state, not
   * permission — so the admin bootstrap discloses tenant → site → rest.
   * "tenant" is grant-derived, so it also lets a tenant admin (not just global) in.
   */
  requiresProvisioning?: "tenant" | "site";
  /**
   * ADR 409: the sales persona sees ONLY what is marked. Default-deny on purpose — a
   * surface added later stays hidden from a presenter until someone decides it belongs
   * in a customer demo. Checked before the global-admin bypass: it is a persona
   * restriction, not a permission.
   */
  salesAllowed?: boolean;
}

export interface GateContext {
  isGlobalAdmin: boolean;
  roleRank: number;
  hasCapability: (cap: Capability) => boolean;
  /** At least one site in scope (ADR 175). */
  hasSite: boolean;
  /** Administers at least one org-unit via grant (ADR 177); for a global admin
   *  this means "at least one tenant exists". */
  canAdminOrgUnits: boolean;
  /** Holds the `sales` realm role (ADR 409). */
  isSales: boolean;
}

/**
 * Whether a gated surface should show, given the user and the system state (ADR 175).
 *
 * The provisioning gate is checked FIRST — before the global-admin bypass —
 * because it is about system state, not permission: a dead system (no site)
 * must not surface operational nav even to a global admin who outranks every
 * permission gate. That ordering is the whole fix for "the admin sees a full
 * menu on an empty system". Permission gates (capability, minRole) then apply
 * to non-admins exactly as before (ADR 120).
 */
export function gateVisible(gate: Gate, ctx: GateContext): boolean {
  // Actionability gates run BEFORE the global-admin bypass (ADR 175/177/178): a
  // surface that can't do anything yet shows to nobody — admin included — so the
  // admin bootstrap discloses progressively (tenant → site → the rest). The
  // "tenant" signal is grant-derived, so it doubles as the org-unit-admin
  // permission gate — a tenant admin sees Sites, a viewer never does.
  if (gate.requiresProvisioning === "tenant" && !ctx.canAdminOrgUnits) return false;
  if (gate.requiresProvisioning === "site" && !ctx.hasSite) return false;
  if (ctx.isSales && !gate.salesAllowed) return false;
  if (ctx.isGlobalAdmin) return true;
  if (gate.requiresCapability && !ctx.hasCapability(gate.requiresCapability)) return false;
  if (gate.requiresAnyCapability && !gate.requiresAnyCapability.some((c) => ctx.hasCapability(c))) return false;
  if (gate.minRole && ctx.roleRank < ROLE_RANK[gate.minRole]) return false;
  return true;
}
